import { deleteIdentity, deleteDevice } from '../services/keyStatePersistence';
import { useAuthStore } from '../store/authStore';
import { useVaultStore } from '../store/vaultStore';
import { useServerColorStore } from '../store/serverColorStore';
import { serverKeyFromServerUrl } from './serverKey';
import { clearLocalCircleData } from './localCircleDataReset';

const REMOVED_ACCESS_KEY = 'circlus.removedCircleServerAccess';

function readRemovedIdentities(): Set<string> {
  try {
    const parsed = JSON.parse(localStorage.getItem(REMOVED_ACCESS_KEY) || '[]');
    return new Set(Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : []);
  } catch {
    return new Set();
  }
}

export function markCircleServerAccessRemoved(identityId: string): boolean {
  const cleanId = identityId.trim();
  if (!cleanId) return false;
  const removed = readRemovedIdentities();
  if (removed.has(cleanId)) return false;
  removed.add(cleanId);
  try {
    localStorage.setItem(REMOVED_ACCESS_KEY, JSON.stringify(Array.from(removed)));
  } catch {
    // The in-memory notification remains useful even when persistence is unavailable.
  }
  return true;
}

export function hasCircleServerAccessRemoved(identityId?: string | null): boolean {
  return !!identityId && readRemovedIdentities().has(identityId.trim());
}

export function clearCircleServerAccessRemoved(identityId: string): void {
  const removed = readRemovedIdentities();
  removed.delete(identityId.trim());
  try {
    localStorage.setItem(REMOVED_ACCESS_KEY, JSON.stringify(Array.from(removed)));
  } catch {
    // ignore
  }
}

export async function deleteRemovedCircleLocalCopy(identityId: string): Promise<{ wasLastCircle: boolean }> {
  const auth = useAuthStore.getState();
  const identity = auth.identities[identityId];
  if (!identity) {
    clearCircleServerAccessRemoved(identityId);
    return { wasLastCircle: false };
  }
  const devicesForIdentity = Object.values(auth.devices).filter((device) => device.identityId === identityId);
  const wasLastCircle = !Object.values(auth.identities).some((entry) => entry.identityId !== identityId)
    && Object.keys(auth.temporaryMemberships).length === 0;
  const vault = useVaultStore.getState();
  const serverKey = identity.serverId || serverKeyFromServerUrl(identity.serverUrl || '') || '';
  if (serverKey) vault.removeServerIdentitiesFromAllContacts(serverKey);
  if (identity.serverId) {
    vault.setPreference(`circle:${identity.serverId}:backupEnabled`, undefined);
    vault.setPreference(`circle:${identity.serverId}:linkPreviewEnabled`, undefined);
    useServerColorStore.getState().setServerColor(identity.serverId, null);
  }
  vault.setCircleAvatar(identityId, null);
  vault.setCircleDisplayName(identityId, null);
  vault.setCircleOwnerIdentityId(identityId, null);
  vault.setCircleOwnerIdentityName(identityId, null);

  await clearLocalCircleData({
    identityId,
    serverId: identity.serverId,
    serverUrl: identity.serverUrl
  });
  await deleteIdentity(identityId);
  for (const device of devicesForIdentity) {
    await deleteDevice(device.deviceId);
  }
  auth.removeIdentity(identityId);
  for (const device of devicesForIdentity) {
    auth.removeDevice(device.deviceId);
  }
  clearCircleServerAccessRemoved(identityId);
  return { wasLastCircle };
}
